import {
  isCarriedNight,
  type LuachDay,
  type LuachEntry,
  type LuachResult,
  scheduledDays,
} from '@app/luach'
import { plain } from '@app/stationery'

/**
 * The reader's layout of a schedule, as questions that have one answer.
 *
 * Nothing here computes a time. Every figure the page shows is a field of the
 * resolved occasion, the same result the printed sheet renders — these decide
 * only which tab a reader starts on, what a day's heading says and in what
 * order the shul's own occasions are offered.
 */

type Occasion = { key: string; labelEn: string; dayCount: number }
type Placed = { occasionKey: string; startDate: string }

/**
 * The Hebrew year an occurrence falls in, as the calendar names it
 * ("15th of Tishrei, 5787"). Read rather than estimated from the civil date,
 * which cannot tell an Elul Shabbos in September from the Tishrei after it.
 */
export function hebrewYearOfOccurrence(o: {
  hebrewDate: string
}): number | null {
  const m = /(\d{4})\s*$/.exec(o.hebrewDate)
  return m ? Number(m[1]) : null
}

/**
 * The shul's own occasions, weekly first.
 *
 * A tab strip is the shul's list and nothing else, so a shul that keeps no
 * Purim sheet offers no Purim tab. The week comes first because it is the one
 * a reader returns to every Friday; the rest keep the order the shul's list
 * already carries, which is the calendar's.
 */
export function readerTabs<T extends { key: string; cadence: string }>(
  occasions: readonly T[],
): T[] {
  return [
    ...occasions.filter((o) => o.cadence === 'weekly'),
    ...occasions.filter((o) => o.cadence !== 'weekly'),
  ]
}

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/**
 * Which sheet a reader lands on: the occasion in progress, else the next one
 * on the calendar.
 *
 * An occurrence is named by its first day, so one already under way — chol
 * hamoed, the second day of Yom Tov — starts before today. Of those whose own
 * day count has not run out, the one that began most recently is read, so a
 * Shabbos inside a longer run does not outlast its own day; with none under
 * way, the next start on the calendar is the one to read.
 */
export function landing<P extends Placed>(
  timeline: readonly P[],
  occasions: readonly Occasion[],
  today: string,
): P | null {
  const running = timeline
    .filter((t) => t.startDate <= today)
    .filter((t) => {
      const days = occasions.find((o) => o.key === t.occasionKey)?.dayCount
      return !!days && addDays(t.startDate, days) > today
    })
    .at(-1)
  return running ?? timeline.find((t) => t.startDate >= today) ?? null
}

const longDate = (iso: string, weekday: boolean) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    ...(weekday ? { weekday: 'long' as const } : {}),
    month: 'long',
    day: 'numeric',
  })

/**
 * The civil date a day's heading states.
 *
 * A section made only of lines carried from the evening before — motzei
 * Shabbos, motzei Yom Tov — is read in the evening those lines happen on, so
 * its civil date is that evening's. The Hebrew date beside it is already the
 * night's, since the Hebrew day begins at nightfall; the civil one has to be
 * told.
 */
export function civilHeading(day: LuachDay): string {
  const evening = allNightEvening(day)
  if (!evening) return longDate(day.date, true)
  return `${weekdayOf(evening)} night, ${longDate(evening, false)}`
}

const weekdayOf = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long' })

/** The civil evening a carried line happens on: the day it resolved against. */
const eveningOf = (day: LuachDay, e: LuachEntry) =>
  addDays(day.date, e.dayOffset - (e.printDayOffset ?? e.dayOffset))

/** The evening a section stands for, when every line in it is carried night. */
const allNightEvening = (day: LuachDay): string | null =>
  day.entries.length > 0 && day.entries.every((e) => isCarriedNight(e))
    ? eveningOf(day, day.entries[0])
    : null

/**
 * Which night a carried line happens on, where its section also holds the
 * day's own lines — "Friday night" above a Shabbos day's shachris. The sheet
 * rules night off from day; a grid headed by the day's civil date has to say
 * it in words, or the night's maariv reads as the evening after.
 */
export function nightOf(day: LuachDay, e: LuachEntry): string | null {
  if (!isCarriedNight(e) || allNightEvening(day)) return null
  return `${weekdayOf(eveningOf(day, e))} night`
}

/** "Friday, September 25 to Sunday, October 4, 2026" — the span the sheet covers. */
export function civilSpan(days: readonly LuachDay[]): string {
  const first = days[0]
  const last = days.at(-1)
  if (!first || !last) return ''
  // A closing motzei section is the evening before its own date.
  const end = allNightEvening(last) ?? last.date
  const year = new Date(`${end}T12:00:00`).getFullYear()
  if (first.date === end) return `${longDate(first.date, true)}, ${year}`
  return `${longDate(first.date, true)} to ${longDate(end, true)}, ${year}`
}

/**
 * The Hebrew year in letters, as the occurrence's first day writes it.
 *
 * Read off the date the engine formatted rather than spelled again here, so
 * the year under the title and the dates under each day cannot disagree. The
 * first day rather than the first listed: an erev in Elul is the year before.
 */
export function hebrewYearOf(result: LuachResult): string {
  const day =
    result.days.find((d) => d.date === result.startDate) ?? result.days[0]
  return day ? (plain(day.hebrewDateHe).split(' ').at(-1) ?? '') : ''
}

/** A Hebrew date without its year — each day heading sits under the year. */
export function hebrewDayMonth(day: LuachDay): string {
  const words = plain(day.hebrewDateHe).split(' ')
  return words.length > 2 ? words.slice(0, -1).join(' ') : words.join(' ')
}

const ORDINAL = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth']

/** Which minyan a line is, in the English the grid reads in. */
export const minyanEn = (n: number | null): string =>
  n && n > 0 ? `${ORDINAL[n - 1] ?? `No. ${n}`} minyan` : ''

/**
 * The days a reader's grid lists, each with the last day it stands for.
 *
 * The sheet lists the days with something scheduled, so a block modeled once —
 * chol hamoed's one shachris for all its weekdays — heads the first of them and
 * the rest drop out. A reader needs to know the block covers them: the days
 * after it that carry nothing and share its name are the ones it stands for.
 */
export function readerDays(
  all: readonly LuachDay[],
): { day: LuachDay; through: LuachDay | null }[] {
  const listed = new Set(scheduledDays([...all]))
  return all.flatMap((day, i) => {
    if (!listed.has(day)) return []
    let through: LuachDay | null = null
    for (const next of all.slice(i + 1)) {
      if (listed.has(next) || next.dayLabelEn !== day.dayLabelEn) break
      through = next
    }
    return [{ day, through }]
  })
}

/** The dates a heading states: one day's, or the run a block stands for. */
export function headingDates(
  day: LuachDay,
  through: LuachDay | null,
): { civil: string; hebrew: string } {
  if (!through) return { civil: civilHeading(day), hebrew: hebrewDayMonth(day) }
  const [fromDay, fromMonth] = splitDayMonth(hebrewDayMonth(day))
  const [toDay, toMonth] = splitDayMonth(hebrewDayMonth(through))
  return {
    civil: `${longDate(day.date, true)} to ${longDate(through.date, true)}`,
    hebrew:
      fromMonth === toMonth
        ? `${fromDay}–${toDay} ${toMonth}`
        : `${hebrewDayMonth(day)} – ${hebrewDayMonth(through)}`,
  }
}

const splitDayMonth = (s: string): [string, string] => {
  const [d, ...month] = s.split(' ')
  return [d ?? '', month.join(' ')]
}
